/**
 * S-30 Deletion grace screen (V1 auth-profiles-gdpr) — the special login
 * state (SM-12 DELETION_GRACE). Dead serious, no humor anywhere in this flow.
 *
 * The grace session is SEALED server-side (middleware default-deny): only the
 * carve-outs work — cancel-delete, logout, export download. grace_due_at
 * arrives on the TokenPair, so the screen reads it straight from
 * POST /auth/refresh (the cookie is set; lib's refreshSession drops the
 * field, so the call is made here and the rotated token is stored back).
 */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { copy, formatCopy } from "@humble/shared";
import { Button, Card, toast } from "../../design/index.js";
import { API_BASE, api, isApiError, refreshSession } from "../../lib/api.js";
import { clearSession, sessionStore, setSession, useStore, type UserRef } from "../../lib/store.js";
import { routeForLifecycle } from "../auth/signup-flow.js";
import "../auth/auth.css";

const SUPPORT_EMAIL = "support@humble.ekaii.fr";

interface GracePair {
  access_token?: string;
  lifecycle?: string;
  grace_due_at?: string;
  user?: UserRef;
}

export function GraceScreen() {
  const status = useStore(sessionStore, (s) => s.status);
  const lifecycle = useStore(sessionStore, (s) => s.lifecycle);
  const navigate = useNavigate();
  const [dueAt, setDueAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) return;
        const pair = (await res.json()) as GracePair;
        if (cancelled || !pair.access_token) return;
        setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
        if (pair.grace_due_at) setDueAt(pair.grace_due_at);
      } catch {
        /* offline — the screen still renders; cancel will surface errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "anon") return <Navigate to="/" replace />;
  if (status === "authed" && lifecycle !== null && lifecycle !== "deletion_grace") {
    return <Navigate to={routeForLifecycle(lifecycle)} replace />;
  }

  const dueText = dueAt
    ? new Date(dueAt).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })
    : "the end of the 14-day grace period";
  const daysLeft = dueAt
    ? Math.max(0, Math.ceil((new Date(dueAt).getTime() - Date.now()) / (24 * 3600 * 1000)))
    : null;

  async function onCancelDeletion() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/me/delete/cancel");
      // Pick up the restored lifecycle (ACTIVE — or BANNED, EC-44) and route.
      await refreshSession();
      const restored = sessionStore.get().lifecycle;
      toast("Deletion canceled. Your account is restored.", "success");
      navigate(routeForLifecycle(restored));
    } catch (err) {
      setError(isApiError(err) ? err.message : "Network error — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    try {
      await api.post("/auth/logout", { body: { all: false } });
    } catch {
      /* revoked already — fine */
    }
    clearSession();
    navigate("/");
  }

  return (
    <div className="auth auth--center" data-screen="S-30">
      <Card padding="lg" style={{ maxWidth: 500 }}>
        <h1 className="auth__title">Account scheduled for deletion</h1>
        <p>{formatCopy(copy.safety.grace_login, { date: dueText })}</p>
        {daysLeft !== null && (
          <p data-testid="grace-countdown">
            {/* serious surface — plain emphasis, tabular digits, no theming */}
            <strong className="type-numeric" style={{ fontSize: "1.15rem" }}>
              {daysLeft} day{daysLeft === 1 ? "" : "s"}
            </strong>{" "}
            until permanent deletion.
          </p>
        )}
        <p className="text-dim" style={{ fontSize: "0.9rem" }}>
          While the grace period runs, your profile is hidden and everything is frozen. Any
          running paid timers (Berserker, Shield window, Boost) keep burning and are not
          refunded. Canceling restores the account to the state it was in before the request.
        </p>
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
        <Button block variant="primary" size="lg" loading={busy} onClick={() => void onCancelDeletion()}>
          Cancel deletion
        </Button>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Button block variant="ghost" onClick={() => void onLogout()}>
            Log out
          </Button>
          <a className="btn btn--ghost btn--block" href={`mailto:${SUPPORT_EMAIL}`}>
            Contact support
          </a>
        </div>
        <p className="text-faint" style={{ fontSize: "0.85rem", marginTop: 12 }}>
          Your data export remains downloadable during the grace period. Requested exports and
          their links were sent to your email.
        </p>
      </Card>
    </div>
  );
}
